/**
 * The scrubber card's read press: open a step's node at full depth in the
 * thread — the claim unclamped, and every act the app offers against it.
 *
 * The card clamps a claim at three lines and reports; this is the press that
 * answers "show me all of it". It walks entirely through doors that already
 * exist — the palette's reveal-then-select, the canvas tap's rail, the strip's
 * poke — because the reading it opens must be THE reading, not a copy raised
 * beside the rail that would drift from the card, the footer and the keys.
 *
 * A `.ts` file for `promotePress.ts`'s reason: the sequencing below is the
 * feature (what moves in the press's own tick, what waits a frame, and when
 * time is touched at all), and only a module a Node test can call keeps it
 * pinned.
 */

import { useStore } from '../core/store'
import { afterSelection } from './promotePress'

/**
 * The press. Returns whether it was taken.
 *
 * Time is touched only when time is what hides the node: the rail draws every
 * step whatever the playhead says, so its card can name a node the scrub has
 * not reached — and selecting that bare would open a reading the canvas cannot
 * show, the exact disagreement `revealNode` exists to prevent one axis over.
 * So the playhead advances to the step, by the rail's own seek arithmetic, and
 * ONLY forward: a reader at live who opens step 5 of 30 is asking to read one
 * claim, not to rewind the graph out from under themselves.
 *
 * The poke waits a frame (`afterSelection`) because the press may be what
 * OPENS the pane: the strip mounts in this commit with the counter already
 * read, and a poke sharing the tick is swallowed — D42's frame, at this door.
 */
export function takeInspect(nodeId: string): boolean {
  const s = useStore.getState()
  const ix = s.session.order.indexOf(nodeId)
  if (ix < 0 || !s.session.nodes[nodeId]) {
    // Pruned between the render that offered the press and the click that took
    // it — `promoteRefusal`'s sentence, because it is the same fact.
    s.toast('That node is no longer on the graph.', 'warn')
    return false
  }
  // Reading takes the wheel from a running replay — the rail's own row press
  // pauses for the same reason: a playhead moving under a chosen step seeks a
  // step nobody chose.
  s.setPlaying(false)
  if (s.playhead !== null && s.playhead < ix + 1) {
    s.setPlayhead(ix + 1 >= s.session.order.length ? null : ix + 1)
  }
  s.revealNode(nodeId)
  s.select(nodeId)
  s.setRail(true)
  afterSelection(nodeId, () => useStore.getState().pokeInspect())
  return true
}
